// Reconnecting WebSocket client shared by the kiosk, clinician workspace and dashboard.

export type ServerMessage =
  | { type: 'hello'; role: string }
  | { type: 'event'; event: string; data: Record<string, unknown> }
  | { type: 'joined'; consultId: string; peers: number }
  | { type: 'peer-joined'; consultId: string; role: string }
  | { type: 'peer-left'; consultId: string }
  | { type: 'signal'; consultId: string; data: SignalData }
  | { type: 'chat'; consultId: string; sender: string; body: string; created_at: string }
  | { type: 'error'; consultId?: string; error: string }
  | { type: 'pong' };

export type SignalData =
  | { kind: 'offer' | 'answer'; sdp: RTCSessionDescriptionInit }
  | { kind: 'ice'; candidate: RTCIceCandidateInit }
  | { kind: 'mode'; mode: 'video' | 'audio' | 'chat' }
  | { kind: 'ready' };

type Listener = (m: ServerMessage) => void;

export class Realtime {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private retry = 0;
  private closed = false;
  private rooms = new Set<string>();
  private url: string;
  connected = false;

  constructor(url: string) {
    this.url = url;
    this.open();
  }

  private open() {
    if (this.closed) return;
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      this.connected = true;
      for (const id of this.rooms) ws.send(JSON.stringify({ type: 'join', consultId: id }));
    };
    ws.onmessage = (e) => {
      const m = JSON.parse(e.data) as ServerMessage;
      for (const l of this.listeners) l(m);
    };
    ws.onclose = () => {
      this.connected = false;
      if (this.closed) return;
      const delay = Math.min(15000, 500 * 2 ** this.retry++);
      setTimeout(() => this.open(), delay);
    };
  }

  on(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  send(msg: Record<string, unknown>) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  join(consultId: string) {
    this.rooms.add(consultId);
    this.send({ type: 'join', consultId });
  }

  leave(consultId: string) {
    this.rooms.delete(consultId);
    this.send({ type: 'leave', consultId });
  }

  close() {
    this.closed = true;
    this.ws?.close();
  }
}
